import {
  Button,
  ConfirmAction,
  DataTable,
  StatusBadge,
  type StatusTone,
} from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { Trash2 } from 'lucide-react';
import { useMemo } from 'react';

import type { ApiKeyEntity } from '../../api/apiKeys.api';

interface ApiKeyListProps {
  keys: ApiKeyEntity[];
  loading: boolean;
  error: Error | null | undefined;
  onRevoke: (id: string) => Promise<void>;
}

function statusFor(k: ApiKeyEntity): { label: string; tone: StatusTone } {
  if (k.revokedAt) return { label: 'Revoked', tone: 'danger' };
  if (k.expiresAt && new Date(k.expiresAt) < new Date()) {
    return { label: 'Expired', tone: 'warning' };
  }
  if (k.isActive) return { label: 'Active', tone: 'success' };
  return { label: 'Inactive', tone: 'neutral' };
}

export function ApiKeyList({ keys, loading, error, onRevoke }: ApiKeyListProps) {
  const columns = useMemo<ColumnDef<ApiKeyEntity>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className="font-medium text-foreground">{row.original.name}</span>,
      },
      {
        accessorKey: 'keyPrefix',
        header: 'Key prefix',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.keyPrefix}
            <span className="opacity-60">***</span>
          </span>
        ),
      },
      {
        accessorKey: 'scopes',
        header: 'Scopes',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.scopes.map((s) => (
              <span
                key={s}
                className={
                  s === '*'
                    ? 'rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary'
                    : 'rounded-full bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground'
                }
              >
                {s === '*' ? 'All Apps' : `${s.slice(0, 8)}…`}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const { label, tone } = statusFor(row.original);
          return <StatusBadge tone={tone}>{label}</StatusBadge>;
        },
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last used',
        cell: ({ row }) =>
          row.original.lastUsedAt ? (
            <span className="text-sm">
              {new Date(row.original.lastUsedAt).toLocaleDateString()}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Never</span>
          ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.createdAt).toLocaleDateString()}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          row.original.revokedAt ? null : (
            <ConfirmAction
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Trash2 /> Revoke
                </Button>
              }
              title="Revoke this API key?"
              description="Any service using this key will lose access immediately. This cannot be undone."
              confirmLabel="Revoke key"
              destructive
              onConfirm={() => onRevoke(row.original.id)}
            />
          ),
      },
    ],
    [onRevoke],
  );

  return <DataTable columns={columns} data={keys} isLoading={loading} error={error} />;
}
